import { BoxV2 as Box, ButtonV2 as Button, Flex } from 'portal-commons';
import { ComponentProps, FC, FunctionComponent, useEffect } from 'react';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import {
  createRequiredValidator,
  createMaxLengthValidator,
  createEmailValidator,
  isValidInput,
} from '../../../Campaigns/validators';
import { MAX_EMAIL_COUNT, MAX_EMAIL_LENGTH } from '../../constants';
import { RemovableTextInput } from '../../../CspRegistration/components';
import Modal from '../Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';

// Define the payload structure for a single email list
export interface EmailEditPayload {
  emails: { email: string }[];
}

interface EmailEditModalProps {
  open: boolean;
  disabled: boolean;
  title: string;
  description?: string;
  emails: string[];
  onClose: () => void;
  onSubmit: (data: EmailEditPayload) => void;
}

const EmailEditModal: FunctionComponent<EmailEditModalProps> = ({
  open,
  disabled,
  title,
  description,
  emails,
  onClose,
  onSubmit,
}) => {
  // Initialize form with dynamic default values
  const form = useForm<EmailEditPayload>({
    mode: 'onChange',
    defaultValues: {
      emails: emails.map((email) => ({ email })),
    },
  });

  const { isValid, isValidating } = form.formState;

  const emailArray = useFieldArray({
    control: form.control,
    name: 'emails',
  });

  const emailRules = {
    validate: {
      isValidInput,
      isRequired: createRequiredValidator('Required field.'),
      withValidMaxLength: createMaxLengthValidator(MAX_EMAIL_LENGTH),
      isEmail: createEmailValidator(),
    },
  };

  // Reset form state only when modal opens
  useEffect(() => {
    if (open) {
      form.reset({
        emails: emails.map((email) => ({ email })),
      });
    }
  }, [open]);

  return (
    <Modal open={open} onClose={onClose} data-testid="emailEditModal">
      <FormProvider {...form}>
        <form
          noValidate
          onSubmit={form.handleSubmit(onSubmit)}
          style={{ width: '100%' }}
          data-testid="emailEditForm"
        >
          <Flex sx={{ flexDirection: 'column', gap: 24, width: '100%' }}>
            {/* Modal Header: title, description */}
            <Flex
              sx={{
                flexDirection: 'column',
                gap: 8,
                color: '#19262A',
                fontWeight: 400,
                lineHeight: 'normal',
              }}
            >
              <Box as="h2" sx={{ fontSize: '24px', fontWeight: '400' }}>
                {title}
              </Box>
              {description && (
                <Box sx={{ fontSize: '14px', color: '#4D5659' }}>
                  {description}
                </Box>
              )}
            </Flex>
            <Box sx={{ backgroundColor: '#CCCFD0', height: 1 }} />

            {/* Emails Form Block */}
            {emailArray.fields.map((field, index) => {
              const isLast = index === emailArray.fields.length - 1;
              return (
                <Controller
                  key={field.id}
                  control={form.control}
                  name={`emails.${index}.email`}
                  rules={emailRules}
                  render={({
                    field: controllerField,
                    fieldState: { error },
                  }) => (
                    <RemovableTextInput
                      data-testid={`emailInput_${index}`}
                      label="Email Address"
                      required
                      maxLength={MAX_EMAIL_LENGTH}
                      value={controllerField.value}
                      onChange={controllerField.onChange}
                      onBlur={controllerField.onBlur}
                      error={!!error}
                      note={error?.message}
                      removable={emailArray.fields.length > 1}
                      onRemoveClick={() => emailArray.remove(index)}
                      sx={{ flexDirection: 'column', gap: 0 }}
                    >
                      {isLast && emailArray.fields.length < MAX_EMAIL_COUNT && (
                        <AddEmailButton
                          onClick={() => emailArray.append({ email: '' })}
                          data-testid="addEmailButton"
                        />
                      )}
                    </RemovableTextInput>
                  )}
                />
              );
            })}

            <Box sx={{ backgroundColor: '#CCCFD0', height: 1 }} />
            {/* Action Buttons */}
            <Flex
              sx={{
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 16,
              }}
            >
              <Button
                variant="outline"
                shape="square"
                size="lg"
                color="secondary"
                onClick={onClose}
                data-testid="cancelButton"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                shape="square"
                size="lg"
                color="secondary"
                disabled={disabled || isValidating || !isValid}
                data-testid="saveButton"
              >
                Save
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};

const AddEmailButton: FC<ComponentProps<typeof Box>> = ({
  sx,
  onClick,
  ...restProps
}) => {
  return (
    <Flex
      data-testid="addEmailButton"
      role="button"
      tabIndex={0}
      sx={{
        alignItems: 'center',
        p: 10,
        color: '#0091B3',
        cursor: 'pointer',
        ...sx,
      }}
      onClick={(e: React.MouseEvent<HTMLDivElement>) => onClick?.(e)}
      {...restProps}
    >
      <Flex
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          width: 22,
          height: 20,
          p: '2px 4px',
          fontSize: '16px',
        }}
      >
        <FontAwesomeIcon icon={faPlus} />
      </Flex>
      <Flex
        sx={{ px: 10, fontSize: '14px', fontWeight: 600, lineHeight: 'normal' }}
      >
        Add Email Address
      </Flex>
    </Flex>
  );
};

export default EmailEditModal;
